import { useState } from "react";
import { Link } from "react-router-dom";
import BookingProgressTimeline from "../components/BookingProgressTimeline";
import { STATUS_COLORS, BRAND_COLORS } from "../lib/bookingStatus";
import { Palette, ArrowLeft } from "lucide-react";

/**
 * Weissblom · Design-Gallery: Timeline-Status
 * ------------------------------------------
 * Single-source-of-truth-Preview für den 4-Farb-Statuscode der
 * Vorbereitungs-Timeline. Wird von der Team-Sidebar (`nav-team-design-gallery`)
 * geöffnet und dient als visuelle Regression-Referenz: wer die Timeline-
 * Farben ändert, sieht hier sofort, ob GRAU/BLAU/GRÜN/ROT strikt getrennt
 * bleiben und ob die Connector-Regel („grün erst wenn linker Schritt fertig")
 * gilt.
 *
 * Alle Buchungen sind Mock-Objekte — kein DB-Zugriff, keine Kunden-Daten.
 * Datum-Basis (`today`) ist immer relativ zur aktuellen Browser-Zeit, damit
 * die Beispiele auch nächstes Jahr noch korrekt sind.
 */

const iso = (offsetDays) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

const MOCK_BOOKINGS = [
  {
    // GRAU — noch nicht gestartet (Anfrage steht, Zeitraum liegt in der Zukunft)
    id: "mock-grey",
    variant: "GRAU · Angefragt / nicht gestartet",
    description:
      "Buchung existiert, aber der Zeitraum liegt in der Zukunft. Keine Aufgabe erledigt, kein aktueller Schritt.",
    booking: {
      id: "mock-grey",
      from_date: iso(14),
      to_date: iso(21),
      status: "requested",
      completed_services: [],
      disabled_standard_services: [],
    },
  },
  {
    // BLAU — aktiver Schritt einer laufenden Buchung
    id: "mock-blue",
    variant: "BLAU · In Bearbeitung",
    description:
      "Buchung ist aktiv (Kunde abwesend, Zeitraum aktuell). Der erste Schritt ist der Fokus-Schritt und wird BLAU markiert; nachfolgende bleiben GRAU.",
    booking: {
      id: "mock-blue",
      from_date: iso(-2),
      to_date: iso(5),
      status: "in_progress",
      completed_services: [],
      disabled_standard_services: [],
    },
  },
  {
    // GRÜN — alle Schritte komplett; Connector-Linien folgen grün
    id: "mock-green",
    variant: "GRÜN · Erledigt",
    description:
      "Alle Punkte der Checkliste abgehakt (oder vom Kunden abgewählt). Kreise und Connector-Linien komplett grün.",
    booking: {
      id: "mock-green",
      from_date: iso(-8),
      to_date: iso(-1),
      status: "completed",
      completed_services: [
        "arrival_cleaning", "linen_change", "plants", "airing", "weekly_checks",
      ],
      disabled_standard_services: [],
    },
  },
  {
    // ROT — Verzögerung: Zeitraum ist vorbei, offene Aufgaben
    id: "mock-red",
    variant: "ROT · Verzögerung",
    description:
      "Zeitraum ist bereits verstrichen, aber die Timeline-Schritte sind nicht abgehakt. Alle Phasen mit verstrichener Deadline werden ROT — mit einem Alert-Symbol im Kreis.",
    booking: {
      id: "mock-red",
      from_date: iso(-10),
      to_date: iso(-3),
      status: "in_progress",
      completed_services: [],
      disabled_standard_services: [],
    },
  },
  {
    // ROT via Property-Attention · aktive Buchung, aber am Objekt selbst hat
    // das Team gerade Handlungsbedarf gemeldet (z. B. Wasserleck) → jede offene
    // Phase soll den kritischen Zustand spiegeln.
    id: "mock-red-attention",
    variant: "ROT · Property-Attention (Feb 2026)",
    description:
      "Buchung läuft normal, aber am verknüpften Objekt hat das Team `status=\"attention\"` gesetzt. Alle noch offenen Timeline-Phasen werden ROT, damit die Störung sofort sichtbar ist — auch wenn die Deadline noch nicht verstrichen ist.",
    booking: {
      id: "mock-red-attention",
      from_date: iso(-2),
      to_date: iso(5),
      status: "in_progress",
      completed_services: ["arrival_cleaning"],
      disabled_standard_services: [],
    },
    property: { id: "mock-attention-prop", status: "attention" },
  },
  {
    // MIXED — echte Progression: Abfahrt fertig, Vorbereitung offen, Ankunft noch nicht dran
    id: "mock-mixed",
    variant: "MIXED · Realer Mittel-Zustand",
    description:
      "Abfahrt ist fertig (GRÜN), Vorbereitung ist der aktuelle Schritt (BLAU), Ankunft steht noch aus (GRAU). Erster Connector wird GRÜN, weil der linke Schritt fertig ist.",
    booking: {
      id: "mock-mixed",
      from_date: iso(-3),
      to_date: iso(4),
      status: "in_progress",
      completed_services: ["arrival_cleaning"],
      disabled_standard_services: [],
    },
  },
];

const LEGEND = [
  { color: STATUS_COLORS.grey,  label: "GRAU",  hex: STATUS_COLORS.grey, meaning: "Angefragt oder noch nicht gestartet" },
  { color: STATUS_COLORS.blue,  label: "BLAU",  hex: STATUS_COLORS.blue, meaning: "In Bearbeitung (aktueller Schritt)" },
  { color: STATUS_COLORS.green, label: "GRÜN",  hex: STATUS_COLORS.green, meaning: "Erledigt (alle Punkte abgehakt)" },
  { color: STATUS_COLORS.red,   label: "ROT",   hex: STATUS_COLORS.red, meaning: "Verzögerung / Problem" },
];

export default function TeamDesignGallery() {
  const [variant, setVariant] = useState("team");

  return (
    <div className="min-h-screen bg-ivory" data-testid="design-gallery-root">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Link
            to="/team"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-navy-dark/60 hover:text-navy"
            data-testid="design-gallery-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Team-Übersicht
          </Link>
        </div>
        <div className="flex items-baseline gap-3 mb-1">
          <Palette className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} />
          <h1 className="text-3xl font-serif text-navy">Design-Gallery · Timeline-Status</h1>
        </div>
        <p className="text-sm text-navy-dark/70 mb-6 max-w-2xl">
          Referenz-Preview für den systemweiten 4-Farb-Statuscode der Vorbereitungs-Timeline.
          Ändert jemand die Timeline-Optik, muss diese Seite exakt so aussehen — sonst ist
          der Farbcode gebrochen.
        </p>

        {/* Farb-Legende */}
        <section className="bg-white border border-navy/10 p-5 mb-8" data-testid="design-gallery-legend">
          <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>Status-Farbcode</div>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {LEGEND.map((l) => (
              <li key={l.label} className="flex items-start gap-3">
                <span
                  className="w-6 h-6 rounded-full border-2 shrink-0 mt-0.5"
                  style={{ background: l.color, borderColor: l.color }}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold text-navy tracking-wide">
                    {l.label} <span className="font-mono text-navy-dark/50">{l.hex}</span>
                  </div>
                  <div className="text-[11.5px] text-navy-dark/70 leading-snug">{l.meaning}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Variant-Umschalter */}
        <div className="flex items-center gap-2 mb-4">
          <span className="label-caps text-navy-dark/60">Variante</span>
          <div className="inline-flex border border-navy/15" role="tablist">
            {["team", "customer"].map((v) => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                data-testid={`design-gallery-variant-${v}`}
                className={`px-3 py-1 text-[11.5px] uppercase tracking-widest transition-colors ${
                  variant === v ? "bg-navy text-ivory" : "bg-white text-navy hover:bg-navy/5"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery */}
        <div className="grid gap-6" data-testid="design-gallery-timelines">
          {MOCK_BOOKINGS.map((mock) => (
            <section
              key={mock.id}
              className="bg-white border border-navy/10"
              data-testid={`design-gallery-item-${mock.id}`}
            >
              <div className="px-5 pt-4 pb-3 border-b border-navy/10 flex items-baseline gap-3">
                <div className="text-[11px] uppercase tracking-widest text-gold">
                  {mock.variant}
                </div>
                <div className="text-[11.5px] text-navy-dark/70 flex-1 leading-snug">
                  {mock.description}
                </div>
              </div>
              <div className="p-0">
                <BookingProgressTimeline booking={mock.booking} property={mock.property || null} variant={variant} />
              </div>
            </section>
          ))}
        </div>

        <p className="text-[11px] text-navy-dark/50 mt-8 border-t border-navy/10 pt-4">
          Mock-Buchungen — kein DB-Zugriff. Datum-Basis relativ zu heute
          (<code className="font-mono">{iso(0)}</code>).
        </p>
      </div>
    </div>
  );
}
